import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, ArrowRight, Loader2, Save, X } from 'lucide-react';

// Mesmo visual do Stepper de agendamentos (client/src/components/agendamentos/components/Stepper.js),
// generalizado para qualquer formulário com múltiplos passos, sempre rolando
// lateralmente (nunca quebra linha) quando há muitos passos.
export function FormStepper({ currentStep, steps, stepErrors, onStepClick }) {
    return (
        <div className="w-full overflow-x-auto px-4 sm:px-8 py-4 mb-8">
            <div className="flex items-center min-w-max">
                {steps.map((label, index) => {
                    const stepNumber = index + 1;
                    const isActive = stepNumber === currentStep;
                    const isComplete = stepNumber < currentStep;
                    const hasError = !!stepErrors?.[index];
                    const isClickable = typeof onStepClick === 'function';
                    return (
                        <Fragment key={stepNumber}>
                            <div
                                className="flex flex-col items-center text-center"
                                style={{ position: 'relative', cursor: isClickable ? 'pointer' : undefined }}
                                onClick={isClickable ? () => onStepClick(stepNumber) : undefined}
                                role={isClickable ? 'button' : undefined}
                                tabIndex={isClickable ? 0 : undefined}
                            >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-300 border-2 shrink-0 ${isComplete ? 'bg-indigo-600 text-white border-indigo-700' : ''} ${isActive ? 'bg-orange-500 text-white border-orange-600 scale-110' : ''} ${!isActive && !isComplete ? 'bg-white text-gray-500 border-gray-300' : ''}`}>
                                    {isComplete ? '✔' : stepNumber}
                                </div>
                                {hasError && (
                                    <span style={{ position: 'absolute', top: 0, right: 8, width: 10, height: 10, background: '#ef4444', borderRadius: '50%', border: '2px solid #fff' }} />
                                )}
                                <p className={`mt-2 text-xs font-semibold transition-all duration-300 w-24 ${isActive || isComplete ? 'text-gray-800' : 'text-gray-500'}`}>{label}</p>
                            </div>
                            {stepNumber < steps.length && (<div className={`flex-auto border-t-2 min-w-[24px] transition-all duration-300 ${isComplete ? 'border-indigo-600' : 'border-gray-300'}`}></div>)}
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
}

FormStepper.propTypes = {
    currentStep: PropTypes.number.isRequired,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
    stepErrors: PropTypes.arrayOf(PropTypes.bool),
    onStepClick: PropTypes.func,
};

// Rodapé padrão de navegação Voltar/Avançar — Voltar some no primeiro passo,
// Avançar vira o botão de salvar (ícone Save + submitLabel) no último.
export function FormStepNavigation({
    currentStep,
    totalSteps,
    onBack,
    onNext,
    onSubmit,
    onCancel,
    cancelLabel = 'Fechar',
    isSubmitting,
    submitLabel = 'Salvar',
    nextDisabled = false,
}) {
    const isLast = currentStep >= totalSteps;
    return (
        <div className="form-step-navigation">
            {onCancel && (
                <button type="button" onClick={onCancel} disabled={isSubmitting} className="action-button action-button-secondary">
                    <X className="icon" /> {cancelLabel}
                </button>
            )}
            {currentStep > 1 && (
                <button type="button" onClick={onBack} disabled={isSubmitting} className="action-button action-button-secondary">
                    <ArrowLeft className="icon" /> Voltar
                </button>
            )}
            {/* ⚠️ key diferente em cada ramo é obrigatório aqui — sem isso o
                React reaproveita o MESMO nó <button> ao trocar de "Avançar"
                (type="button") pro botão final (type="submit") na transição
                pro último passo. Mudar o atributo `type` de um elemento
                dentro do próprio handler de clique desse elemento faz o
                navegador reavaliar a ação padrão do clique já em cima do
                type="submit" novo, e o clique em "Avançar" acaba também
                submetendo o formulário — pulando o último passo sem o
                usuário nunca ver seu conteúdo. Com key diferente, o React
                desmonta o botão antigo e monta um nó novo, então não há
                type="submit" nenhum vivo para herdar a ação do clique atual. */}
            {!isLast ? (
                <button key="step-next" type="button" onClick={onNext} disabled={isSubmitting || nextDisabled} className="action-button action-button-primary">
                    Avançar <ArrowRight className="icon" />
                </button>
            ) : (
                <button key="step-submit" type={onSubmit ? 'button' : 'submit'} onClick={onSubmit} disabled={isSubmitting} className="action-button action-button-primary">
                    {isSubmitting ? <Loader2 className="icon animate-spin" /> : <Save className="icon" />}
                    {submitLabel}
                </button>
            )}
        </div>
    );
}

FormStepNavigation.propTypes = {
    currentStep: PropTypes.number.isRequired,
    totalSteps: PropTypes.number.isRequired,
    onBack: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    cancelLabel: PropTypes.string,
    isSubmitting: PropTypes.bool,
    submitLabel: PropTypes.string,
    nextDisabled: PropTypes.bool,
};

export default FormStepper;
